import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ExternalLink, Trash2 } from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { powerBiUsageApi } from '../../services/powerBiUsageApi';
import { powerBiReportUrl } from '../../types/powerBiUsage';
import type { PbiReportDetail, PbiReviewStatus, PbiUsageWindow } from '../../types/powerBiUsage';
import { PBI_REVIEW_ORDER, PBI_REVIEW_META, toReviewStatus } from './status';
import { fmtNum, fmtDate, fmtDateTime } from './format';

interface Props {
    reportId: string;
    window: PbiUsageWindow;
    onClose: () => void;
    width: number;
    onWidthChange: (w: number) => void;
    canEdit: boolean;
    refreshKey: number;
    onPickStatus: (detail: PbiReportDetail, status: PbiReviewStatus) => void;
}

/** Zero-fill the sparse daily series so the chart shows the quiet days too. */
function zeroFill(series: Array<{ day: string; views: number }>, windowDays: number): Array<{ day: string; views: number }> {
    const byDay = new Map(series.map(s => [s.day, s.views]));
    const days: Array<{ day: string; views: number }> = [];
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - (windowDays - 1));
    for (let i = 0; i < windowDays; i++) {
        const d = new Date(start);
        d.setUTCDate(start.getUTCDate() + i);
        const key = d.toISOString().slice(0, 10);
        days.push({ day: key, views: byDay.get(key) ?? 0 });
    }
    return days;
}

export function PowerBiUsageDetailDrawer({
    reportId, window: usageWindow, onClose, width, onWidthChange, canEdit, refreshKey, onPickStatus,
}: Props) {
    const [detail, setDetail] = useState<PbiReportDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const dragging = useRef(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        powerBiUsageApi.getDetail(reportId, usageWindow)
            .then(d => { if (!cancelled) setDetail(d); })
            .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load detail'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [reportId, usageWindow, refreshKey]);

    // Drag-to-resize (percent of the split container = the drawer's parent flex row).
    const onDragStart = useCallback((e: React.PointerEvent) => {
        e.preventDefault();
        dragging.current = true;
        const container = (e.currentTarget as HTMLElement).parentElement?.parentElement;
        const onMove = (ev: PointerEvent) => {
            if (!dragging.current || !container) return;
            const rect = container.getBoundingClientRect();
            const pct = ((rect.right - ev.clientX) / rect.width) * 100;
            onWidthChange(Math.min(85, Math.max(40, pct)));
        };
        const onUp = () => {
            dragging.current = false;
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
    }, [onWidthChange]);

    const windowDays = usageWindow === 'all' ? 90 : Number(usageWindow);
    const chartData = detail ? zeroFill(detail.dailySeries, windowDays) : [];
    const url = detail ? powerBiReportUrl(detail) : null;
    const review = toReviewStatus(detail?.review.status);

    return (
        <div className="relative flex min-w-0 flex-col rounded-lg border border-gray-200 bg-white" style={{ width: `${width}%` }}>
            {/* Resize handle */}
            <div
                onPointerDown={onDragStart}
                className="absolute -left-1.5 top-0 z-10 h-full w-3 cursor-col-resize"
                title="Drag to resize"
            />
            {/* Header */}
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-3">
                <div className="min-w-0">
                    <h2 className="flex items-center gap-1.5 truncate text-base font-semibold text-gray-900" title={detail?.name}>
                        {detail?.isDeleted && <Trash2 className="h-4 w-4 shrink-0 text-red-400" />}
                        {detail?.name ?? '…'}
                    </h2>
                    <p className="truncate text-xs text-gray-500">
                        {detail?.workspaceName ?? ''}
                        {detail?.reportType ? ` · ${detail.reportType}` : ''}
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    {url && (
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open in Power BI"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-blue-600"
                        >
                            <ExternalLink className="h-4 w-4" />
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
                {loading && (
                    <div className="flex h-40 items-center justify-center">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    </div>
                )}
                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
                )}
                {!loading && detail && (
                    <>
                        {/* Review block */}
                        <section className="rounded-lg border border-gray-200 p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Review</h3>
                                {canEdit && (
                                    <div className="flex items-center gap-1">
                                        {PBI_REVIEW_ORDER.map(s => {
                                            const meta = PBI_REVIEW_META[s];
                                            const Icon = meta.icon;
                                            const active = review === s;
                                            return (
                                                <button
                                                    key={s}
                                                    type="button"
                                                    disabled={active}
                                                    onClick={() => onPickStatus(detail, s)}
                                                    className={`inline-flex h-7 items-center gap-1 rounded-full border px-2 text-xs font-medium transition ${
                                                        active
                                                            ? `${meta.badgeClass} ring-2 ring-blue-200`
                                                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <Icon className={`h-3.5 w-3.5 ${meta.iconClass}`} />
                                                    {meta.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            {detail.review.status && (
                                <p className="mt-2 text-xs text-gray-600">
                                    {detail.review.note && <span className="italic">“{detail.review.note}” — </span>}
                                    {detail.review.by} · {fmtDateTime(detail.review.at)}
                                </p>
                            )}
                            {detail.reviewHistory.length > 0 && (
                                <ul className="mt-2 space-y-1 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
                                    {detail.reviewHistory.map((h, i) => (
                                        <li key={i}>
                                            {fmtDateTime(h.createdAt)} · {h.actorEmail}: {h.oldStatus ?? '—'} → {h.newStatus}
                                            {h.statusNote ? ` (“${h.statusNote}”)` : ''}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {/* Totals */}
                        <section className="grid grid-cols-4 gap-2 text-center">
                            {[
                                { label: '30d views', value: detail.totals.views30 },
                                { label: '90d views', value: detail.totals.views90 },
                                { label: 'All-time views', value: detail.totals.viewsAllTime },
                                { label: 'All-time users', value: detail.totals.usersAllTime },
                            ].map(t => (
                                <div key={t.label} className="rounded-lg border border-gray-200 p-2">
                                    <p className="text-lg font-bold text-gray-900">{fmtNum(t.value)}</p>
                                    <p className="text-[11px] text-gray-500">{t.label}</p>
                                </div>
                            ))}
                        </section>

                        {/* Daily views chart (UTC days) */}
                        <section>
                            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Daily views {usageWindow === 'all' ? '(last 90 days)' : `(last ${windowDays} days)`} — UTC
                            </h3>
                            <div className="h-40 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                        <XAxis
                                            dataKey="day"
                                            tick={{ fontSize: 10, fill: '#9ca3af' }}
                                            tickFormatter={(d: string) => d.slice(5)}
                                            interval="preserveStartEnd"
                                            tickLine={false}
                                        />
                                        <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} tickLine={false} axisLine={false} />
                                        <Tooltip
                                            formatter={(value) => [fmtNum(Number(value ?? 0)), 'Views']}
                                            labelFormatter={(d) => `${String(d ?? '')} (UTC)`}
                                        />
                                        <Bar dataKey="views" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </section>

                        {/* Top users */}
                        <section>
                            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Top users (window)</h3>
                            {detail.topUsers.length === 0 ? (
                                <p className="text-sm text-gray-400">No views in the selected window.</p>
                            ) : (
                                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                                    {detail.topUsers.map(u => (
                                        <li key={u.userId} className="flex items-center justify-between px-3 py-1.5 text-sm">
                                            <span className="truncate text-gray-700">{u.userId}</span>
                                            <span className="ml-2 shrink-0 text-xs text-gray-500">
                                                {fmtNum(u.views)} views · {fmtDate(u.lastViewedAt)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {/* Recent events (all activity types) */}
                        <section>
                            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Recent activity</h3>
                            {detail.recentEvents.length === 0 ? (
                                <p className="text-sm text-gray-400">No recorded activity yet.</p>
                            ) : (
                                <div className="overflow-x-auto rounded-lg border border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-100 text-xs">
                                        <thead className="bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500">
                                            <tr>
                                                <th className="px-2 py-1.5">Time (UTC)</th>
                                                <th className="px-2 py-1.5">User</th>
                                                <th className="px-2 py-1.5">Activity</th>
                                                <th className="px-2 py-1.5">Via</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {detail.recentEvents.map((e, i) => (
                                                <tr key={i}>
                                                    <td className="whitespace-nowrap px-2 py-1 text-gray-600">{fmtDateTime(e.creationTime)}</td>
                                                    <td className="max-w-[12rem] truncate px-2 py-1 text-gray-700">{e.userId ?? '—'}</td>
                                                    <td className="px-2 py-1 text-gray-700">{e.activity}</td>
                                                    <td className="px-2 py-1 text-gray-500">
                                                        {[e.distributionMethod, e.appName].filter(Boolean).join(' · ') || '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        {/* Metadata */}
                        <section className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                            <span className="text-gray-400">First seen</span><span>{fmtDate(detail.firstSeenAt)}</span>
                            <span className="text-gray-400">Last seen in inventory</span><span>{fmtDate(detail.lastSeenAt)}</span>
                            <span className="text-gray-400">Created (Power BI)</span><span>{fmtDate(detail.createdDateTime)}</span>
                            <span className="text-gray-400">Modified (Power BI)</span>
                            <span>{fmtDate(detail.modifiedDateTime)}{detail.modifiedBy ? ` · ${detail.modifiedBy}` : ''}</span>
                        </section>
                    </>
                )}
            </div>
        </div>
    );
}
